import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Table } from '@ohpen/enso-ui';

import { ConfigTableActions } from '@config/components';
import { useConfigContext } from '@config/context';
import { CONFIG_PAGINATION, CONFIG_SCROLL } from '@config/utils';
import type { FlagRow, FlagValue } from '../interfaces';
import { flagColumns, getValueTagMappings, VALUE_LIST_NAME } from '../tableConfig';

export const FeatureFlagsList = (): ReactElement => {
  const { t } = useTranslation();
  const { configuration, flagDescriptions } = useConfigContext();

  const valueList = useMemo(() => ({ on: t('config.on'), off: t('config.off') }), [t]);

  const dataSource: FlagRow[] = useMemo(
    () =>
      Object.entries(configuration?.featureToggles ?? {})
        .map(([flag, enabled]) => {
          const key: FlagValue = enabled ? 'on' : 'off';

          return { flag, value: { key, value: valueList[key] }, description: flagDescriptions?.[flag] };
        })
        .sort((a, b) => a.flag.localeCompare(b.flag)),
    [configuration, flagDescriptions, valueList],
  );

  const valueLists = useMemo(() => ({ [VALUE_LIST_NAME]: valueList }), [valueList]);

  return (
    <Table
      id="configFeatureFlags"
      rowKey="flag"
      dataSource={dataSource}
      columns={flagColumns}
      valueLists={valueLists}
      tagMappings={getValueTagMappings()}
      pagination={CONFIG_PAGINATION}
      scroll={CONFIG_SCROLL}
      itemsLabel={t('config.sections.featureFlags')}
    >
      <ConfigTableActions />
    </Table>
  );
};
